/**
 * Plan & Billing page — current plan overview and upgrade flow.
 *
 * Two plans only: Free and Pro. Monthly billing only.
 */

import {
  Badge, BlockStack, Button, Card, Grid, InlineStack,
  Layout, List, Page, Text,
} from "@shopify/polaris";
import { PRO_MONTHLY_PRICE } from "@fbc/shared";
import { useBilling } from "../hooks/useBilling.js";

type PlanCard = {
  name: string;
  price: string;
  description: string;
  features: string[];
};

const PLANS: PlanCard[] = [
  {
    name: "Free",
    price: "$0 / month",
    description: "Everything most stores need to understand performance.",
    features: [
      "Revenue, orders, AOV, unique customers",
      "Profit dashboard and top-product breakdown",
      "Returns analytics",
      "Customer cohorts, LTV, and RFM segments",
      "90 days of history",
      "Manual cost entry for up to 20 SKUs",
    ],
  },
  {
    name: "Pro",
    price: `${PRO_MONTHLY_PRICE} / month`,
    description: "Everything in Free, plus depth, breadth, and exports.",
    features: [
      "Sales by country, state, and city world map",
      "CSV and PDF report exports",
      "Monthly expense tracking by category",
      "Unlimited per-variant COGS",
      "Unlimited history",
      "Cancel anytime — no annual lock-in",
    ],
  },
];

export function Billing() {
  const { plan, manage } = useBilling();
  const isPro = plan === "pro";

  return (
    <Page
      title="Plan & Billing"
      subtitle={
        isPro
          ? `You're on the Pro plan — ${PRO_MONTHLY_PRICE}/month, billed monthly.`
          : `You're on the Free plan. Pro is ${PRO_MONTHLY_PRICE}/month with a 14-day free trial.`
      }
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Current plan</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">{isPro ? "Pro" : "Free"}</Badge>
                  <Text as="span" tone="subdued">
                    {isPro ? `${PRO_MONTHLY_PRICE}/month` : "No charges"}
                  </Text>
                </InlineStack>
              </BlockStack>
            </Card>

            <Grid>
              {PLANS.map((p) => {
                const isCurrent = (p.name === "Pro") === isPro;
                return (
                  <Grid.Cell
                    key={p.name}
                    columnSpan={{ xs: 6, sm: 6, md: 3, lg: 6, xl: 6 }}
                  >
                    <Card>
                      <BlockStack gap="300">
                        <InlineStack align="space-between" blockAlign="center">
                          <InlineStack gap="200" blockAlign="center">
                            <Text as="h3" variant="headingMd">{p.name}</Text>
                            {isCurrent && <Badge tone="success">Current</Badge>}
                          </InlineStack>
                          <Text as="span" tone={isCurrent ? "subdued" : undefined}>
                            {p.price}
                          </Text>
                        </InlineStack>

                        <Text as="p" tone="subdued">{p.description}</Text>

                        <List type="bullet">
                          {p.features.map((f) => (
                            <List.Item key={f}>{f}</List.Item>
                          ))}
                        </List>

                        {p.name === "Pro" && !isPro && (
                          <Button
                            variant="primary"
                            onClick={() => { void manage(); }}
                          >
                            {`Upgrade to Pro — ${PRO_MONTHLY_PRICE}/mo`}
                          </Button>
                        )}
                        {p.name === "Pro" && isPro && (
                          <Button onClick={() => { void manage(); }}>
                            Manage subscription
                          </Button>
                        )}
                      </BlockStack>
                    </Card>
                  </Grid.Cell>
                );
              })}
            </Grid>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
